import {
  Component,
  inject,
  input,
  computed,
  forwardRef,
  signal,
} from '@angular/core';

import {
  ControlValueAccessor,
  FormBuilder,
  Validators,
  ReactiveFormsModule,
  FormGroup,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';

// --- UI & Layout Imports ---
import { CardComponent } from '../layout/card/card.component';
import { HeadingComponent } from '../typography/heading/heading.component';
import { FormFieldComponent } from '../forms/form-field/form-field.component';

// --- Custom Form Control Imports ---
import { AddressFormComponent } from '../forms/address-form/address-form.component';
import { AppointmentControlComponent } from '../forms/appointment-control/appointment-control.component';
import { ContactInputComponent } from '../forms/contact-input/contact-input.component';
import { KeyValue } from '../../../core/models/app-types.model';
import { KeyValueArrayInputComponent } from '../../../shared/ui/forms/key-value-array-input/key-value-array-input.component';
import { TextareaComponent } from '../forms/textarea/textarea.component';
import { InputComponent } from '../forms/input/input.component';
import { NumberInputComponent } from '../forms/number-input/number-input.component';
import { ButtonGroupComponent } from '../forms/button-group/button-group.component';
import { Stop } from '../../../core/models/stop.model';
import { StopType } from '../../../core/enums/stop-type.enum';
import { Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Address } from '../../../core/models/address.model';
import { ContactInfo } from '../../../core/models/contact-info.model';
import { AppType } from '../../../core/models/app-types.model';
import { ReferenceOptions } from '../../../core/enums/reference.enum';
import { ItemOptions } from '../../../core/enums/item.enum';

@Component({
  selector: 'app-add-stop-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CardComponent,
    HeadingComponent,
    FormFieldComponent,
    AddressFormComponent,
    AppointmentControlComponent,
    ContactInputComponent,
    TextareaComponent,
    InputComponent,
    NumberInputComponent,
    ButtonGroupComponent,
    KeyValueArrayInputComponent
],
  templateUrl: './add-stop-form.component.html',
  providers: [
    // Add the provider to register this component as a form control
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AddStopFormComponent),
      multi: true,
    }],
})
export class AddStopFormComponent implements ControlValueAccessor {
  private readonly fb = inject(FormBuilder);
  private valueChangesSub?: Subscription;

  stopNumber = input(1);
  protected stopForm: FormGroup;

  private stopTypeSignal = signal<StopType | null | undefined>('pickup');

  // This computed signal will now be perfectly reactive.
  readonly stopLabel = computed(() => {
    const type = this.stopTypeSignal();
    switch (type) {
      case 'pickup':
        return 'Shipper';
      case 'delivery':
        return 'Consignee';
      case 'both':
        return 'Shipper & Consignee';
      default:
        return 'Location';
    }
  });

  constructor() {
    // Form initialization is correct here.
    this.stopForm = this.fb.group({
      stopType: ['pickup' as StopType, [Validators.required]],
      companyName: [null],
      address: [null as Address | null, [Validators.required]],
      contactInfo: [null as ContactInfo | null],
      operatingHours: [''],
      appointment: [null as any, [Validators.required]],
      instructions: [''],
      notes: [''],
      weight: [null as number | null],
      referenceNumbers: [[] as KeyValue[]],
      itemCounts: [[] as KeyValue[]],
    });
  }

  // --- ControlValueAccessor Implementation ---
  onChange: (value: Partial<Stop> | null) => void = () => {};
  onTouched: () => void = () => {};

  writeValue(value: Stop | null): void {
    // FIX: Always reset the form first to clear stale data from the previous stop.
    // Provide default values for fields that need them (like arrays).
    this.stopForm.reset(
      {
        stopType: 'pickup',
        referenceNumbers: [],
        itemCounts: [],
      },
      { emitEvent: false }
    );

    // Now, patch the form with the new value if it exists.
    if (value) {
      this.stopForm.patchValue(value, { emitEvent: false });
      this.stopTypeSignal.set(value.stopType);
    } else {
      // If the value is null, ensure the signal is also reset.
      this.stopTypeSignal.set('pickup');
    }
  }

  registerOnChange(fn: (value: Partial<Stop> | null) => void): void {
    this.onChange = fn;
    this.valueChangesSub?.unsubscribe();

    this.valueChangesSub = this.stopForm.valueChanges
      .pipe(
        // When the user changes the form, update the signal.
        tap((formValue) => this.stopTypeSignal.set(formValue.stopType))
        // FIX: No longer need the toStopValue mapping function.
        // The form value is already in the correct shape.
      )
      .subscribe(this.onChange);
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.stopForm.disable();
    } else {
      this.stopForm.enable();
    }
  }
  // --- End ControlValueAccessor ---

  stopTypes: { label: string; value: StopType }[] = [
    { label: 'Pickup', value: 'pickup' },
    { label: 'Delivery', value: 'delivery' },
    { label: 'Both', value: 'both' }];

  referenceTypes: AppType[] = ReferenceOptions.map((t) => ({
    id: t.value,
    label: t.label,
  }));
  itemTypes: AppType[] = ItemOptions.map((t) => ({
    id: t.value,
    label: t.label,
  }));

}
